"use client";

import { CalendarDays } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Calendar } from "~/components/ui/calendar";
import { cn } from "~/lib/utils";
interface DatePickerProps {
  value: Date | null;
  onChange: (day: Date) => void;
  disabled?: (d: Date) => boolean;
  rangeStart?: Date | null;
  rangeEnd?: Date | null;
  isRange?: boolean;
  defaultOpen?: boolean;
  className?: string;
  startMonth?: Date;
  endMonth?: Date;
}
export function DatePicker({
  value,
  onChange,
  disabled,
  rangeStart,
  rangeEnd,
  isRange,
  defaultOpen = false,
  className,
  startMonth,
  endMonth
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (defaultOpen) {
      const timer = setTimeout(() => setOpen(true), 50);
      return () => clearTimeout(timer);
    }
  }, [defaultOpen]);
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);
  function formatDisplay(d: Date | null) {
    if (!d) return "Select date";
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  }
  return <div ref={containerRef} className={cn("relative flex-1 min-w-0", className)}>
      <button type="button" onClick={() => setOpen(o => !o)} className={cn("flex h-11 w-full items-center justify-between gap-2 rounded-full border bg-white px-4 text-left shadow-none outline-none transition-colors min-w-0", open ? "border-[var(--blue)] ring-1 ring-[var(--blue)]" : "border-[var(--ink-20)] hover:border-[var(--blue)] focus:border-[var(--blue)] focus:ring-1 focus:ring-[var(--blue)]")}>
        <span className="truncate text-[14px] font-medium text-[var(--ink)]">
          {formatDisplay(value)}
        </span>
        <CalendarDays className="size-4 shrink-0 text-[#9CA3AF]" strokeWidth={1.5} />
      </button>

      {open && <div data-ezspot-calendar
    // Removed overflow-hidden and changed p-2 to p-3 to fix the bottom cutoff
    className="absolute left-0 top-[calc(100%+8px)] z-20 rounded-[16px] border border-[var(--ink-20)] bg-white shadow-[0_8px_30px_rgb(0,0,0,0.12)] p-3" style={{
      ["--ring" as string]: "var(--blue-light)"
    }}>
          <Calendar mode="single" selected={value ?? undefined} onSelect={day => {
        if (day) {
          onChange(day);
          setOpen(false);
        }
      }} disabled={disabled} startMonth={startMonth} endMonth={endMonth} modifiers={isRange ? {
        range_start: rangeStart ? [rangeStart] : [],
        range_end: rangeEnd ? [rangeEnd] : [],
        range_middle: rangeStart && rangeEnd ? {
          after: rangeStart,
          before: rangeEnd
        } : []
      } : undefined} className="[&_button[data-selected-single=true]]:!bg-[var(--blue)] [&_button[data-selected-single=true]]:!text-white [&_button[data-selected-single=true]]:!rounded-full" classNames={{
        head_cell: "text-[var(--blue)] font-medium text-[12px] w-9 uppercase",
        range_start: "relative isolate z-0 rounded-l-full bg-[var(--blue-pale)] after:absolute after:inset-y-0 after:right-0 after:w-4 after:bg-[var(--blue-pale)] [&>button]:bg-[var(--blue)] [&>button]:text-white [&>button]:rounded-full",
        range_end: "relative isolate z-0 rounded-r-full bg-[var(--blue-pale)] after:absolute after:inset-y-0 after:left-0 after:w-4 after:bg-[var(--blue-pale)] [&>button]:bg-[var(--blue)] [&>button]:text-white [&>button]:rounded-full",
        range_middle: "rounded-none bg-[var(--blue-pale)] [&>button]:!bg-transparent [&>button]:text-[var(--ink)]",
        today: "rounded-full ring-2 ring-[var(--blue)] [&>button]:text-[var(--blue)] data-[selected=true]:ring-0"
      }} />
        </div>}
    </div>;
}